import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import PermissionApi from "../../api/PermissionApi";

/* =========================================
   ALL AVAILABLE MODULES
========================================= */

export const ALL_MODULES = [
  { key: "dashboard", label: "Dashboard" },

  { key: "profile", label: "Profile" },

  { key: "type", label: "Type Management" },

  { key: "group", label: "Group Management" },

  { key: "source", label: "Source Management" },

  { key: "role", label: "Role Management" },

  { key: "user", label: "User Management" },

  { key: "leadassign", label: "Lead Assignment" },

  { key: "lead", label: "Lead Master" },

  { key: "leadfinal", label: "Final Leads" },

  { key: "followup", label: "Follow-ups" },

  { key: "chat", label: "Chat" },

  { key: "email", label: "Email" },

  { key: "template", label: "Templates" },

  { key: "selltemplate", label: "Sell Templates" },

  { key: "projectmodules", label: "Project Modules" },

  { key: "project-progress", label: "Project Progress" },

  { key: "project", label: "Projects" },

  { key: "disposition", label: "Disposition" },


  /* =========================================
     TESTER MODULES
  ========================================= */

  {
    key: "assigned-testing",
    label: "Assigned Testing",
  },

  {
    key: "test-cases",
    label: "Test Cases",
  },

  {
    key: "bugs",
    label: "Bug Management",
  },

  {
    key: "testing-reports",
    label: "Testing Reports",
  },


  /* =========================================
     DEVELOPER MODULES
  ========================================= */

  {
    key: "assigned-bugs",
    label: "Assigned Bugs",
  },

  /* =========================================
   SALES MODULES
========================================= */

{
  key: "my-availability",
  label: "My Availability",
},

{
  key: "my-meetings",
  label: "My Meetings",
},
];




/* =========================================
   FETCH PERMISSIONS BY ROLE
========================================= */

export const fetchPermissionsByRole =
  createAsyncThunk(
    "permissions/fetchByRole",

    async (roleId, { rejectWithValue }) => {
      try {
        const res =
          await PermissionApi.getByRole(roleId);

        return {
          roleId,

          data:
            res.data ?? {},
        };

      } catch (err) {

        return rejectWithValue(
          err.message ||
          "Failed to fetch permissions"
        );

      }
    }
  );


/* =========================================
   SAVE PERMISSIONS
========================================= */

export const savePermissions =
  createAsyncThunk(
    "permissions/bulkSet",

    async (
      {
        roleId,
        permissions,
      },

      {
        rejectWithValue,
      }
    ) => {

      try {

        const permissionsArray =
          Object.entries(
            permissions
          ).map(
            ([module, perms]) => ({
              module,

              canRead:
                perms.canRead ?? false,

              canWrite:
                perms.canWrite ?? false,

              canDelete:
                perms.canDelete ?? false,
            })
          );


        const res =
          await PermissionApi.bulkSet({
            roleId,

            permissions:
              permissionsArray,
          });

        return res;

      } catch (err) {

        return rejectWithValue(
          err.message ||
          "Failed to save permissions"
        );

      }

    }
  );


/* =========================================
   SLICE
========================================= */

const permissionSlice =
  createSlice({

    name:
      "permissions",


    initialState: {

      byRole: {},

      loadingRoleId:
        null,

      saving:
        false,

      error:
        null,
    },


    reducers: {

      /* =====================================
         TOGGLE PERMISSION
      ===================================== */

      togglePermission(
        state,
        action
      ) {

        const {
          roleId,
          module,
          permKey,
        } =
          action.payload;


        if (
          !state.byRole[roleId]
        ) {

          state.byRole[roleId] =
            {};

        }


        if (
          !state.byRole[roleId][module]
        ) {

          state.byRole[roleId][module] = {

            canRead:
              false,

            canWrite:
              false,

            canDelete:
              false,
          };

        }


        state
          .byRole[roleId]
          [module]
          [permKey] =
          !state
            .byRole[roleId]
            [module]
            [permKey];

      },


      clearError(state) {

        state.error =
          null;

      },

    },


    extraReducers:
      (builder) => {

        builder


          /* FETCH */

          .addCase(
            fetchPermissionsByRole.pending,

            (
              state,
              action
            ) => {

              state.loadingRoleId =
                action.meta.arg;

              state.error =
                null;

            }
          )


          .addCase(
            fetchPermissionsByRole.fulfilled,

            (
              state,
              action
            ) => {

              const {
                roleId,
                data,
              } =
                action.payload;


              state.byRole[roleId] =
                data;


              state.loadingRoleId =
                null;

            }
          )


          .addCase(
            fetchPermissionsByRole.rejected,

            (
              state,
              action
            ) => {

              state.loadingRoleId =
                null;

              state.error =
                action.payload;

            }
          )


          /* SAVE */

          .addCase(
            savePermissions.pending,

            (
              state
            ) => {

              state.saving =
                true;

              state.error =
                null;

            }
          )


          .addCase(
            savePermissions.fulfilled,

            (
              state
            ) => {

              state.saving =
                false;

            }
          )


          .addCase(
            savePermissions.rejected,

            (
              state,
              action
            ) => {

              state.saving =
                false;

              state.error =
                action.payload;

            }
          );

      },

  });


export const {
  togglePermission,
  clearError,
} =
  permissionSlice.actions;


/* =========================================
   SELECTORS
========================================= */

export const selectPermissionsForRole =
  (roleId) =>
  (state) =>
    state.permissions.byRole[
      roleId
    ] ?? null;


export const selectPermissionsLoading =
  (roleId) =>
  (state) =>
    state.permissions.loadingRoleId ===
    roleId;


export const selectPermissionsSaving =
  (state) =>
    state.permissions.saving;


/* =========================================
   EXPORT
========================================= */

export default
  permissionSlice.reducer;